/**
 * One portal address: member.nodus.<env> (owner, 2026-10-16).
 *
 * A site's own domain (e.g. member.l26.se) resolves through internal DNS to the on-site copy of this app, which
 * knows its site (runtime `SITE_ID`). That copy sends the visitor on to member.nodus.<env>, same path, with
 * `?site=<id>`; the portal there remembers the site and shows its logo and name before anyone signs in.
 * Links nodus builds carry `?site=` too (backend `core.portal_links`).
 */

const STORAGE_KEY = "nodus-portal-site";

/** member.nodus.<tld> for the API domain this app talks to (api.nodus.se → member.nodus.se). */
export function memberHost(apiDomain: string): string {
  const tld = apiDomain.match(/nodus\.(\w+)$/)?.[1] ?? "se";
  return `member.nodus.${tld}`;
}

/** The member.nodus address to send a visitor on a site's own domain to, or null to stay. */
export function vanityRedirect(
  location: Pick<Location, "hostname" | "pathname" | "search" | "hash" | "protocol">,
  runtimeSiteId: string | undefined,
  apiDomain: string,
): string | null {
  const host = location.hostname;
  if (!runtimeSiteId) return null; // the shared deployment: already the one address
  if (/^(member|tenant)\.nodus\./.test(host)) return null;
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost")) return null;
  const params = new URLSearchParams(location.search);
  params.set("site", runtimeSiteId);
  return `https://${memberHost(apiDomain)}${location.pathname}?${params.toString()}${location.hash}`;
}

/** Keep `?site=<id>` from the address (then drop it from the bar) for the login page's branding. */
export function rememberSiteFromUrl(): void {
  const url = new URL(window.location.href);
  const site = url.searchParams.get("site");
  if (!site || !/^\d{1,9}$/.test(site)) return;
  try {
    localStorage.setItem(STORAGE_KEY, site);
  } catch {
    /* private mode: the branding just stays generic */
  }
  url.searchParams.delete("site");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

/** The site the branding is for: remembered from a `?site=` link, if any. */
export function rememberedSiteId(): string | undefined {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
